import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, BookOpen, Inbox } from "lucide-react";
import { curSym } from "@/lib/currency";

const EMPTY_ART = { category_id: "", name_fr: "", name_en: "", synonyms: "", flags: "", quote_required: false, approval_required: false, unit: "article", image: "", length_cm: "", width_cm: "", height_cm: "", weight_est: "", allowed_modes: "both" };
const MODE_LABELS = { air: ["Aérien / Express", "bg-sky-100 text-sky-700"], sea: ["Maritime", "bg-blue-600 text-white"], both: ["Air + Maritime", "bg-secondary text-primary"] };

export const AdminCatalog = () => {
  const [data, setData] = useState({ categories: [], articles: [] });
  const [unlisted, setUnlisted] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [q, setQ] = useState("");
  const [artOpen, setArtOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [art, setArt] = useState(EMPTY_ART);
  const [newCat, setNewCat] = useState("");

  const load = () => {
    api.get("/admin/catalog").then(({ data }) => setData(data));
    api.get("/admin/catalog/unlisted").then(({ data }) => setUnlisted(data));
  };
  useEffect(() => { load(); }, []);

  const saveArt = async () => {
    if (!art.name_fr || !art.category_id) { toast.error("Nom et catégorie requis"); return; }
    const payload = { ...art, synonyms: art.synonyms.split(",").map((s) => s.trim()).filter(Boolean),
      flags: art.flags.split(",").map((s) => s.trim()).filter(Boolean),
      length_cm: Number(art.length_cm) || 0, width_cm: Number(art.width_cm) || 0,
      height_cm: Number(art.height_cm) || 0, weight_est: Number(art.weight_est) || 0 };
    if (editId) await api.put(`/admin/catalog/articles/${editId}`, payload);
    else await api.post("/admin/catalog/articles", payload);
    toast.success(editId ? "Article mis à jour" : "Article créé — aucun code nécessaire");
    setArtOpen(false); setArt(EMPTY_ART); setEditId(null); load();
  };

  const toggleArt = async (a, active) => { await api.put(`/admin/catalog/articles/${a.id}`, { active }); load(); };
  const toggleCat = async (c, active) => { await api.put(`/admin/catalog/categories/${c.id}`, { active }); load(); };
  const renameCat = async (c) => {
    const name = prompt("Nouveau nom de la catégorie :", c.name_fr);
    if (name) { await api.put(`/admin/catalog/categories/${c.id}`, { name_fr: name }); load(); }
  };
  const addCat = async () => {
    if (!newCat.trim()) return;
    await api.post("/admin/catalog/categories", { name_fr: newCat, sort_order: data.categories.length });
    setNewCat(""); toast.success("Catégorie créée"); load();
  };
  const promote = async (a) => { await api.post(`/admin/catalog/articles/${a.id}/promote`); toast.success("Ajouté au catalogue Colis2Go"); load(); };
  const promoteUnlisted = async (g, extra = {}) => {
    const cat = data.categories.find((c) => c.key === "misc") || data.categories[0];
    try {
      await api.post("/admin/catalog/unlisted/add-to-catalog", { category_id: cat.id, name_fr: g.name, synonyms: [], flags: [], ...extra });
      toast.success(`« ${g.name} » ajouté au catalogue`); load();
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); return false; }
  };

  // Traitement d'un devis « article non répertorié » (route, détails, CBM, montant)
  const [proc, setProc] = useState(null);
  const openProc = (g) => setProc({
    g, qty: g.detail?.qty || 1, weight: g.detail?.weight_est || "",
    container: g.detail?.container || "", L: g.detail?.length_cm || "",
    W: g.detail?.width_cm || "", H: g.detail?.height_cm || "",
    article_id: "", amount: "", note: "",
  });
  const procCbm = proc ? Math.round(((Number(proc.L) || 0) * (Number(proc.W) || 0) * (Number(proc.H) || 0)) / 1e6 * (Number(proc.qty) || 1) * 10000) / 10000 : 0;
  const procCur = proc?.g?.detail?.hub_country === "Royaume-Uni" ? "GBP" : "EUR";
  const sendQuote = async () => {
    if (!Number(proc.amount)) { toast.error("Saisissez le montant du devis"); return; }
    try {
      const { data: r } = await api.post("/admin/catalog/unlisted/quote", { name: proc.g.name, amount: Number(proc.amount), currency: procCur, note: proc.note });
      toast.success(`Devis envoyé à ${r.clients_notifies} client(s)`); setProc(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const customs = data.articles.filter((a) => a.scope === "partner");
  const globals = data.articles.filter((a) => a.scope === "global")
    .filter((a) => statusFilter === "all" || (statusFilter === "active" ? a.active : !a.active))
    .filter((a) => catFilter === "all" || a.category_id === catFilter)
    .filter((a) => !q || a.name_fr.toLowerCase().includes(q.toLowerCase()));
  const COUNT_LABELS = { active: "Articles actifs du catalogue", inactive: "Articles désactivés du catalogue", all: "Tous les articles du catalogue" };

  return (
    <div className="space-y-8">
      {/* Catégories */}
      <div>
        <h3 className="font-heading font-bold flex items-center gap-2"><BookOpen className="h-5 w-5 text-accent" /> Catégories ({data.categories.length})</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {data.categories.map((c) => (
            <div key={c.id} data-testid={`cat-${c.id}`} className={`border px-3 py-1.5 text-sm flex items-center gap-2 ${c.active ? "border-border" : "border-dashed opacity-50"}`}>
              <button className="font-medium hover:text-accent" onClick={() => renameCat(c)}>{c.name_fr}</button>
              <Switch checked={c.active} onCheckedChange={(v) => toggleCat(c, v)} />
            </div>
          ))}
          <div className="flex items-center gap-1">
            <Input data-testid="newcat-input" className="rounded-sm h-9 w-44" placeholder="Nouvelle catégorie" value={newCat} onChange={(e) => setNewCat(e.target.value)} />
            <Button size="sm" data-testid="newcat-add" variant="outline" className="rounded-sm" onClick={addCat}><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      </div>

      {/* Articles */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading font-bold" data-testid="catalog-count">{COUNT_LABELS[statusFilter]} ({globals.length})</h3>
          <Button size="sm" data-testid="admin-add-article" className="rounded-sm bg-accent text-white gap-1" onClick={() => { setArt(EMPTY_ART); setEditId(null); setArtOpen(true); }}><Plus className="h-4 w-4" /> Ajouter un article</Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="artfilter-status" className="rounded-sm w-52"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Articles actifs</SelectItem>
              <SelectItem value="inactive">Articles désactivés</SelectItem>
              <SelectItem value="all">Tous les articles</SelectItem>
            </SelectContent>
          </Select>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="artfilter-cat" className="rounded-sm w-64"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Toutes les catégories</SelectItem>{data.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_fr}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="artfilter-q" className="rounded-sm w-64" placeholder="Rechercher…" value={q} onChange={(e) => setQ(e.target.value)} />
          <span className="text-xs text-muted-foreground self-center" data-testid="artfilter-count">{globals.length} résultat(s)</span>
        </div>
        <div className="mt-3 overflow-x-auto max-h-[480px] overflow-y-auto border border-border">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Article</TableHead><TableHead>Synonymes</TableHead><TableHead>Mode autorisé</TableHead><TableHead>Attributs</TableHead><TableHead>Devis obligatoire</TableHead><TableHead>Actif</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {globals.map((a) => (
                <TableRow key={a.id} data-testid={`admin-art-${a.id}`}>
                  <TableCell className="font-semibold">{a.name_fr}<div className="text-xs text-muted-foreground">{a.name_en}</div></TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{(a.synonyms || []).join(", ") || "—"}</TableCell>
                  <TableCell data-testid={`art-mode-${a.id}`}>{(() => { const [l, cls] = MODE_LABELS[a.allowed_modes || "both"]; return <Badge className={`rounded-sm text-[10px] ${cls}`}>{l}</Badge>; })()}</TableCell>
                  <TableCell>{(a.flags || []).map((f) => <Badge key={f} className="rounded-sm bg-secondary text-primary mr-1 text-[10px]">{f}</Badge>)}</TableCell>
                  <TableCell>{a.quote_required ? <Badge className="rounded-sm bg-amber-100 text-amber-700">Devis</Badge> : "—"}</TableCell>
                  <TableCell><Switch data-testid={`art-active-${a.id}`} checked={a.active} onCheckedChange={(v) => toggleArt(a, v)} /></TableCell>
                  <TableCell><Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" data-testid={`art-edit-${a.id}`}
                    onClick={() => { setEditId(a.id); setArt({ ...EMPTY_ART, ...a, synonyms: (a.synonyms || []).join(", "), flags: (a.flags || []).join(", ") }); setArtOpen(true); }}>Modifier</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Articles personnalisés partenaires */}
      <div>
        <h3 className="font-heading font-bold">Articles personnalisés des partenaires ({customs.length})</h3>
        <div className="mt-2 space-y-2">
          {customs.map((a) => (
            <div key={a.id} data-testid={`custom-art-${a.id}`} className="border border-border bg-card p-3 flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="font-medium">{a.name_fr} <span className="text-muted-foreground">· créé par {a.owner_name}</span></span>
              <Button size="sm" data-testid={`promote-${a.id}`} className="rounded-sm bg-primary text-white" onClick={() => promote(a)}>Ajouter au catalogue Colis2Go</Button>
            </div>
          ))}
          {customs.length === 0 && <p className="text-xs text-muted-foreground">Aucun article personnalisé en attente.</p>}
        </div>
      </div>

      {/* Articles non répertoriés */}
      <div>
        <h3 className="font-heading font-bold flex items-center gap-2"><Inbox className="h-5 w-5 text-accent" /> Articles non répertoriés (demandes clients)</h3>
        <div className="mt-2 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nom</TableHead><TableHead>Trajet</TableHead><TableHead>Détails</TableHead><TableHead>Demandes</TableHead><TableHead>Clients</TableHead><TableHead>Dernière demande</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {unlisted.map((g, i) => (
                <TableRow key={i} data-testid={`unlisted-${i}`}>
                  <TableCell className="font-semibold">{g.name}</TableCell>
                  <TableCell className="text-xs">{g.detail?.hub_country ? `${g.detail.hub_country} → ${g.detail.dest_country}${g.detail.dest_city ? ` — ${g.detail.dest_city}` : ""} · ${g.detail.mode === "air" ? "Aérien" : g.detail.mode === "sea" ? "Maritime" : "—"}` : "—"}</TableCell>
                  <TableCell className="text-xs">{`${g.detail?.qty || 1} × ${g.detail?.weight_est ? `${g.detail.weight_est} kg` : "poids ?"}${g.detail?.container ? ` · ${g.detail.container}` : ""}`}</TableCell>
                  <TableCell>{g.requests}</TableCell>
                  <TableCell>{g.customers}</TableCell>
                  <TableCell className="text-xs">{(g.last_request || "").slice(0, 10)}</TableCell>
                  <TableCell><Button size="sm" data-testid={`unlisted-process-${i}`} className="rounded-sm bg-accent text-white" onClick={() => openProc(g)}>Traiter le devis</Button></TableCell>
                </TableRow>
              ))}
              {unlisted.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">Aucune demande d&rsquo;article non répertorié.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog traitement de devis — article non répertorié */}
      <Dialog open={!!proc} onOpenChange={(v) => { if (!v) setProc(null); }}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg" data-testid="unlisted-proc-dialog">
          {proc && (<>
            <DialogHeader><DialogTitle>Traiter le devis — « {proc.g.name} »</DialogTitle></DialogHeader>
            <div className="text-xs border border-border bg-secondary/50 p-2.5 space-y-0.5" data-testid="proc-route">
              <p><b>Trajet :</b> {proc.g.detail?.hub_country || "?"} → {proc.g.detail?.dest_country || "?"}{proc.g.detail?.dest_city ? ` — ${proc.g.detail.dest_city}` : ""} · {proc.g.detail?.mode === "air" ? "Aérien / Express" : proc.g.detail?.mode === "sea" ? "Maritime" : "Mode ?"}</p>
              <p><b>Client :</b> {proc.g.detail?.customer_name || "—"} · {proc.g.requests} demande(s)</p>
              {proc.g.detail?.description && <p><b>Description :</b> {proc.g.detail.description}</p>}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Quantité</Label><Input data-testid="proc-qty" type="number" className="rounded-sm h-8" value={proc.qty} onChange={(e) => setProc({ ...proc, qty: e.target.value })} /></div>
              <div><Label className="text-xs">Poids (kg)</Label><Input data-testid="proc-weight" type="number" className="rounded-sm h-8" value={proc.weight} onChange={(e) => setProc({ ...proc, weight: e.target.value })} /></div>
              <div><Label className="text-xs">Récipient</Label>
                <select data-testid="proc-container" className="w-full h-8 border border-border bg-card px-2 text-sm" value={proc.container} onChange={(e) => setProc({ ...proc, container: e.target.value })}>
                  <option value="">—</option><option value="carton">Carton</option><option value="fut">Fût</option><option value="valise">Valise</option><option value="sac">Sac</option><option value="autre">Autre</option>
                </select></div>
              <div><Label className="text-xs">Longueur (cm)</Label><Input data-testid="proc-length" type="number" className="rounded-sm h-8" value={proc.L} onChange={(e) => setProc({ ...proc, L: e.target.value })} /></div>
              <div><Label className="text-xs">Largeur (cm)</Label><Input data-testid="proc-width" type="number" className="rounded-sm h-8" value={proc.W} onChange={(e) => setProc({ ...proc, W: e.target.value })} /></div>
              <div><Label className="text-xs">Hauteur (cm)</Label><Input data-testid="proc-height" type="number" className="rounded-sm h-8" value={proc.H} onChange={(e) => setProc({ ...proc, H: e.target.value })} /></div>
            </div>
            {procCbm > 0 && <p className="text-xs font-semibold" data-testid="proc-cbm">Volume : {procCbm} m³</p>}
            <div><Label className="text-xs">Rattacher à un article existant du catalogue (facultatif)</Label>
              <select data-testid="proc-article" className="w-full h-9 border border-border bg-card px-2 text-sm" value={proc.article_id} onChange={(e) => setProc({ ...proc, article_id: e.target.value })}>
                <option value="">— Aucun (nom libre du client) —</option>
                {data.articles.filter((a) => a.scope === "global" && a.active).map((a) => <option key={a.id} value={a.id}>{a.name_fr}</option>)}
              </select></div>
            <div className="grid grid-cols-2 gap-2 items-end">
              <div><Label className="text-xs">Montant du devis ({curSym(procCur)}) — fixe, kg, unité ou CBM</Label>
                <Input data-testid="proc-amount" type="number" className="rounded-sm h-9" value={proc.amount} onChange={(e) => setProc({ ...proc, amount: e.target.value })} /></div>
              <div><Label className="text-xs">Note pour le client (facultatif)</Label>
                <Input data-testid="proc-note" className="rounded-sm h-9" value={proc.note} onChange={(e) => setProc({ ...proc, note: e.target.value })} /></div>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" data-testid="proc-add-catalog" className="rounded-sm" onClick={async () => {
                const ok = await promoteUnlisted(proc.g, { length_cm: Number(proc.L) || 0, width_cm: Number(proc.W) || 0, height_cm: Number(proc.H) || 0, weight_est: Number(proc.weight) || 0 });
                if (ok) setProc(null);
              }}>Ajouter cet article au catalogue</Button>
              <Button data-testid="proc-send-quote" className="rounded-sm bg-accent text-white" onClick={sendQuote}>Envoyer le devis au client</Button>
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Dialog article */}
      <Dialog open={artOpen} onOpenChange={setArtOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifier l'article" : "Nouvel article du catalogue"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Nom FR</Label><Input data-testid="adm-art-namefr" className="rounded-sm" value={art.name_fr} onChange={(e) => setArt({ ...art, name_fr: e.target.value })} /></div>
            <div><Label>Nom EN</Label><Input className="rounded-sm" value={art.name_en} onChange={(e) => setArt({ ...art, name_en: e.target.value })} /></div>
            <div className="col-span-2"><Label>Catégorie</Label>
              <Select value={art.category_id} onValueChange={(v) => setArt({ ...art, category_id: v })}>
                <SelectTrigger data-testid="adm-art-cat" className="rounded-sm"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>{data.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_fr}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Mode autorisé</Label>
              <Select value={art.allowed_modes || "both"} onValueChange={(v) => setArt({ ...art, allowed_modes: v })}>
                <SelectTrigger data-testid="adm-art-mode" className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Les deux (Air + Maritime)</SelectItem>
                  <SelectItem value="air">Aérien / Express uniquement</SelectItem>
                  <SelectItem value="sea">Maritime uniquement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Synonymes (séparés par des virgules, FR/EN)</Label><Input data-testid="adm-art-syn" className="rounded-sm" placeholder="tv, télé, television" value={art.synonyms} onChange={(e) => setArt({ ...art, synonyms: e.target.value })} /></div>
            <div className="col-span-2"><Label>Attributs spéciaux (fragile, battery, liquid, dangerous…)</Label><Input className="rounded-sm" value={art.flags} onChange={(e) => setArt({ ...art, flags: e.target.value })} /></div>
            <div><Label>Image (URL)</Label><Input className="rounded-sm" value={art.image} onChange={(e) => setArt({ ...art, image: e.target.value })} /></div>
            <div><Label>Poids indicatif (kg)</Label><Input type="number" className="rounded-sm" value={art.weight_est} onChange={(e) => setArt({ ...art, weight_est: e.target.value })} /></div>
            <div><Label>Dimensions L×l×H (cm)</Label>
              <div className="flex gap-1">
                <Input type="number" className="rounded-sm" placeholder="L" value={art.length_cm} onChange={(e) => setArt({ ...art, length_cm: e.target.value })} />
                <Input type="number" className="rounded-sm" placeholder="l" value={art.width_cm} onChange={(e) => setArt({ ...art, width_cm: e.target.value })} />
                <Input type="number" className="rounded-sm" placeholder="H" value={art.height_cm} onChange={(e) => setArt({ ...art, height_cm: e.target.value })} />
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <label className="flex items-center gap-2 text-sm"><Switch checked={art.quote_required} onCheckedChange={(v) => setArt({ ...art, quote_required: v })} /> Devis obligatoire</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={art.approval_required} onCheckedChange={(v) => setArt({ ...art, approval_required: v })} /> Approbation requise</label>
            </div>
          </div>
          <DialogFooter><Button data-testid="adm-art-save" className="rounded-sm bg-accent text-white" onClick={saveArt}>Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
